import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from 'services/api';
import {
  listarCatalogoProdutos,
  type ProdutoAfiliado,
  type ProdutoAfiliadoFilters,
} from 'services/produtosAfiliados';
import {
  ListaDetalheResumo,
  ListaResponse,
  Presente,
  PresentesResponse,
} from './AdminListaPresentesPage.types';

export type DialogMode = 'create' | 'edit';

const emptyForm = {
  nome: '',
  descricao: '',
  valor: '',
  imagemUrl: '',
  categoria: '',
  disponivel: true,
};

type FormState = typeof emptyForm;

type FiltrosPresentes = {
  search: string;
  categoria: string;
  parceiro: string;
  disponivel: 'todos' | 'true' | 'false';
  minValor: string;
  maxValor: string;
};

export const useAdminListaPresentesRules = (listaId?: string) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lista, setLista] = useState<ListaDetalheResumo | null>(null);
  const [presentes, setPresentes] = useState<Presente[]>([]);
  const [presentesLoadingId, setPresentesLoadingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<DialogMode>('create');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [currentPresente, setCurrentPresente] = useState<Presente | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [reloading, setReloading] = useState(false);

  const [catalogoOpen, setCatalogoOpen] = useState(false);
  const [catalogoLoading, setCatalogoLoading] = useState(false);
  const [catalogoProdutos, setCatalogoProdutos] = useState<ProdutoAfiliado[]>([]);
  const [catalogoError, setCatalogoError] = useState('');
  const catalogoSearchRef = useRef('');
  const [catalogoFiltroSearch, setCatalogoFiltroSearch] = useState('');
  const debounceBuscaRef = useRef<number | null>(null);
  const [catalogoAdicionandoId, setCatalogoAdicionandoId] = useState<string | null>(null);
  const [catalogoSuccess, setCatalogoSuccess] = useState('');
  const [catalogoCategorias, setCatalogoCategorias] = useState<string[]>([]);
  const [catalogoFiltroCategoria, setCatalogoFiltroCategoria] = useState('');
  const [catalogoFiltroFaixa, setCatalogoFiltroFaixa] = useState<{ min?: number; max?: number }>(
    {}
  );
  const [catalogoSelecionados, setCatalogoSelecionados] = useState<Set<string>>(() => new Set());
  const [catalogoPage, setCatalogoPage] = useState(1);
  const [catalogoPageSize, setCatalogoPageSize] = useState(12);
  const [catalogoTotal, setCatalogoTotal] = useState(0);
  const [catalogoTotalPages, setCatalogoTotalPages] = useState(0);
  const [catalogoFaixaPreco, setCatalogoFaixaPreco] = useState<{
    min: number | null;
    max: number | null;
  } | null>(null);

  const precoChips = useMemo(
    () => [
      { label: 'Até R$ 80,00', max: 80 },
      { label: 'R$ 80,00 - R$ 200,00', min: 80, max: 200 },
      { label: 'R$ 200,00 - R$ 500,00', min: 200, max: 500 },
      { label: 'R$ 500,00 - R$ 800,00', min: 500, max: 800 },
      { label: 'R$ 800,00 - R$ 1.200,00', min: 800, max: 1200 },
      { label: 'Acima de R$ 1.200,00', min: 1200, max: undefined },
    ],
    []
  );

  const filtrosPresentesDefault: FiltrosPresentes = {
    search: '',
    categoria: '',
    parceiro: '',
    disponivel: 'todos',
    minValor: '',
    maxValor: '',
  };

  const [filtrosPresentes, setFiltrosPresentes] = useState<FiltrosPresentes>({
    ...filtrosPresentesDefault,
  });

  const tituloLista = useMemo(() => {
    if (!lista) return '';
    return lista.tituloLista || lista.template?.titulo || 'Lista de presentes';
  }, [lista]);

  const presentesAfiliadosIds = useMemo(() => {
    const ids = new Set<string>();
    presentes.forEach((presenteItem) => {
      if (presenteItem.produtoAfiliadoId) {
        ids.add(presenteItem.produtoAfiliadoId);
      }
    });
    return ids;
  }, [presentes]);

  const categoriasDisponiveis = useMemo(() => {
    const categorias = new Set<string>();
    presentes.forEach((presenteItem) => {
      if (!presenteItem.categoria) return;
      categorias.add(presenteItem.categoria);
    });
    return Array.from(categorias).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [presentes]);

  const parceirosDisponiveis = useMemo(() => {
    const parceiros = new Set<string>();
    presentes.forEach((presenteItem) => {
      if (presenteItem.produtoAfiliado?.parceiro) {
        parceiros.add(presenteItem.produtoAfiliado.parceiro);
      }
    });
    return Array.from(parceiros).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [presentes]);

  const faixaPrecoAtivaChave = useMemo(() => {
    const min = filtrosPresentes.minValor.trim();
    const max = filtrosPresentes.maxValor.trim();
    return `${min}|${max}`;
  }, [filtrosPresentes.maxValor, filtrosPresentes.minValor]);

  const presentesFiltrados = useMemo(() => {
    const termo = filtrosPresentes.search.trim().toLowerCase();
    const categoriaFiltro = filtrosPresentes.categoria.trim().toLowerCase();
    const parceiroFiltro = filtrosPresentes.parceiro.trim().toLowerCase();
    const somenteDisponiveis =
      filtrosPresentes.disponivel !== 'todos' ? filtrosPresentes.disponivel === 'true' : null;

    const parseValor = (valor: string) => {
      if (!valor) return null;
      const limpo = valor.replace(/\s/g, '').replace(',', '.');
      const numero = Number(limpo);
      return Number.isNaN(numero) ? null : numero;
    };

    const minValor = parseValor(filtrosPresentes.minValor);
    const maxValor = parseValor(filtrosPresentes.maxValor);

    return presentes.filter((presenteItem) => {
      if (termo) {
        const alvo = `${presenteItem.nome} ${presenteItem.descricao ?? ''} ${
          presenteItem.categoria ?? ''
        } ${presenteItem.produtoAfiliado?.parceiro ?? ''}`
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
        const termoNormalizado = termo.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        if (!alvo.includes(termoNormalizado)) return false;
      }

      if (categoriaFiltro) {
        const categoriaItem = (presenteItem.categoria ?? '').toLowerCase();
        if (categoriaItem !== categoriaFiltro) return false;
      }

      if (parceiroFiltro) {
        const parceiroItem = (presenteItem.produtoAfiliado?.parceiro ?? '').toLowerCase();
        if (!parceiroItem.includes(parceiroFiltro)) return false;
      }

      if (somenteDisponiveis !== null && presenteItem.disponivel !== somenteDisponiveis) {
        return false;
      }

      if (minValor !== null && presenteItem.valor < minValor) return false;
      if (maxValor !== null && presenteItem.valor > maxValor) return false;

      return true;
    });
  }, [filtrosPresentes, presentes]);

  const filtrosAplicadosResumo = useMemo(() => {
    const resumo: string[] = [];
    if (filtrosPresentes.search) resumo.push(`Busca: "${filtrosPresentes.search}"`);
    if (filtrosPresentes.categoria) resumo.push(`Categoria: ${filtrosPresentes.categoria}`);
    if (filtrosPresentes.parceiro) resumo.push(`Afiliado: ${filtrosPresentes.parceiro}`);
    if (filtrosPresentes.disponivel !== 'todos') {
      resumo.push(
        filtrosPresentes.disponivel === 'true' ? 'Apenas disponíveis' : 'Apenas indisponíveis'
      );
    }
    const parseValor = (valor: string) => {
      const numero = Number(valor.replace(',', '.'));
      return Number.isNaN(numero) ? null : numero;
    };
    const minValor = parseValor(filtrosPresentes.minValor);
    const maxValor = parseValor(filtrosPresentes.maxValor);
    const formatar = (valor: number) =>
      new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
    if (minValor !== null) resumo.push(`Valor min: ${formatar(minValor)}`);
    if (maxValor !== null) resumo.push(`Valor máx: ${formatar(maxValor)}`);
    return resumo.length ? resumo.join(' • ') : 'Nenhum filtro aplicado';
  }, [filtrosPresentes]);

  const catalogoResumoPaginacao = useMemo(() => {
    if (catalogoTotal === 0 || catalogoProdutos.length === 0) {
      return '';
    }
    const inicio = (catalogoPage - 1) * catalogoPageSize + 1;
    const fim = inicio + catalogoProdutos.length - 1;
    return `${inicio}–${fim} de ${catalogoTotal} produtos`;
  }, [catalogoPage, catalogoPageSize, catalogoProdutos.length, catalogoTotal]);

  const catalogoFaixaPrecoLabel = useMemo(() => {
    if (!catalogoFaixaPreco) return '';
    const { min, max } = catalogoFaixaPreco;
    const formatar = (valor: number) =>
      new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
        .format(valor)
        .replace('R$', 'R$ ');

    if (min != null && max != null) {
      return `Faixa disponível: ${formatar(min)} até ${formatar(max)}`;
    }
    if (min != null) {
      return `Faixa disponível: a partir de ${formatar(min)}`;
    }
    if (max != null) {
      return `Faixa disponível: até ${formatar(max)}`;
    }
    return '';
  }, [catalogoFaixaPreco]);

  const catalogoFaixaPrecoAtivaChave = useMemo(() => {
    const minKey =
      typeof catalogoFiltroFaixa.min === 'number' ? catalogoFiltroFaixa.min.toFixed(2) : '';
    const maxKey =
      typeof catalogoFiltroFaixa.max === 'number' ? catalogoFiltroFaixa.max.toFixed(2) : '';
    return `${minKey}|${maxKey}`;
  }, [catalogoFiltroFaixa.max, catalogoFiltroFaixa.min]);

  const carregarDados = useCallback(async () => {
    if (!listaId) {
      setError('Lista não informada.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const [listaRes, presentesRes] = await Promise.all([
        api.get<ListaResponse>(`/listas/${listaId}`),
        api.get<PresentesResponse>(`/presentes/lista/${listaId}/presentes`),
      ]);

      setLista(listaRes.data.lista);
      setPresentes(presentesRes.data.presentes ?? []);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao carregar dados da lista';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [listaId]);

  useEffect(() => {
    void carregarDados();
  }, [carregarDados]);

  useEffect(() => {
    if (!success) return;
    const timeout = window.setTimeout(() => setSuccess(''), 4000);
    return () => window.clearTimeout(timeout);
  }, [success]);

  const atualizarFiltroPresentes = (campo: keyof FiltrosPresentes, valor: string) => {
    setFiltrosPresentes((prev) => ({ ...prev, [campo]: valor }));
  };

  const handleFiltroValorChange = (campo: 'minValor' | 'maxValor', valor: string) => {
    const sanitizado = valor.replace(/[^0-9.,]/g, '');
    atualizarFiltroPresentes(campo, sanitizado);
  };

  const aplicarCategoriaRapida = (categoria: string) => {
    setFiltrosPresentes((prev) => ({ ...prev, categoria }));
  };

  const aplicarParceiroRapido = (parceiro: string) => {
    setFiltrosPresentes((prev) => ({ ...prev, parceiro }));
  };

  const aplicarFaixaPrecoRapida = (min?: number, max?: number) => {
    const minStr = typeof min === 'number' ? min.toFixed(2) : '';
    const maxStr = typeof max === 'number' ? max.toFixed(2) : '';
    setFiltrosPresentes((prev) => ({ ...prev, minValor: minStr, maxValor: maxStr }));
  };

  const limparFiltrosPresentes = () => {
    setFiltrosPresentes({ ...filtrosPresentesDefault });
  };

  useEffect(() => {
    const state = location.state as { mensagemSucesso?: string; abrirCatalogo?: boolean } | null;
    if (!state) return;

    if (state.mensagemSucesso) {
      setSuccess(state.mensagemSucesso);
    }

    let updatedSearch = location.search;
    if (state.abrirCatalogo) {
      const params = new URLSearchParams(location.search);
      params.set('catalogo', '1');
      updatedSearch = `?${params.toString()}`;
      setCatalogoOpen(true);
      setSearchParams(params, { replace: true });
    }

    navigate(`${location.pathname}${updatedSearch}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate, setSearchParams]);

  const catalogoQueryFlag = searchParams.get('catalogo');

  useEffect(() => {
    if (catalogoQueryFlag === '1') {
      setCatalogoOpen(true);
    }
  }, [catalogoQueryFlag]);

  const abrirDialogCriar = () => {
    setDialogMode('create');
    setForm(emptyForm);
    setCurrentPresente(null);
    setDialogOpen(true);
  };

  const abrirDialogEditar = (presente: Presente) => {
    setDialogMode('edit');
    setCurrentPresente(presente);
    setForm({
      nome: presente.nome,
      descricao: presente.descricao ?? '',
      valor: presente.valor.toString().replace('.', ','),
      imagemUrl: presente.imagemUrl ?? '',
      categoria: presente.categoria ?? '',
      disponivel: presente.disponivel,
    });
    setDialogOpen(true);
  };

  const fecharDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const handleFormChange = (campo: keyof FormState, valor: string | boolean) => {
    setForm((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const salvarPresente = async () => {
    if (!listaId) return;

    const nome = form.nome.trim();
    const valorSanitizado = String(form.valor).replace(/\s/g, '').replace(',', '.');
    const valorNumerico = Number(valorSanitizado);

    if (!nome || Number.isNaN(valorNumerico) || valorNumerico <= 0) {
      setError('Informe nome e valor válido para o presente.');
      return;
    }

    setSaving(true);

    const payload = {
      nome,
      descricao: form.descricao.trim() || undefined,
      valor: valorNumerico,
      imagemUrl: form.imagemUrl.trim() || undefined,
      categoria: form.categoria.trim() || undefined,
      disponivel: form.disponivel,
    };

    try {
      if (dialogMode === 'create') {
        await api.post(`/presentes/lista/${listaId}/presentes`, payload);
        setSuccess('Presente adicionado com sucesso!');
      } else if (dialogMode === 'edit' && currentPresente) {
        await api.put(`/presentes/lista/${listaId}/presentes/${currentPresente.id}`, payload);
        setSuccess('Presente atualizado com sucesso!');
      }

      setDialogOpen(false);
      await carregarDados();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao salvar presente';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const alterarDisponibilidade = async (presente: Presente, disponivel: boolean) => {
    if (!listaId) return;

    try {
      setPresentesLoadingId(presente.id);
      setError('');
      const resposta = await api.put(`/presentes/lista/${listaId}/presentes/${presente.id}`, {
        disponivel,
      });
      const presenteAtualizado: Presente | undefined = resposta.data?.presente;

      if (presenteAtualizado) {
        setPresentes((prev) =>
          prev.map((item) => (item.id === presenteAtualizado.id ? presenteAtualizado : item))
        );
      } else {
        await carregarDados();
      }

      setSuccess(disponivel ? 'Presente reativado.' : 'Presente desativado.');
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao atualizar disponibilidade';
      setError(message);
    } finally {
      setPresentesLoadingId(null);
    }
  };

  const removerPresente = async (presente: Presente) => {
    if (!listaId) return;
    const jaPresenteado = (presente.transacoesConfirmadas ?? 0) > 0;
    const confirmar = window.confirm(
      jaPresenteado
        ? 'Este presente já foi presenteado. Ele será desativado e não poderá ser comprado novamente. Deseja continuar?'
        : `Deseja realmente remover o presente "${presente.nome}"?`
    );
    if (!confirmar) return;

    try {
      setDeletingId(presente.id);
      setError('');
      const resposta = await api.delete(`/presentes/lista/${listaId}/presentes/${presente.id}`);
      const presenteAtualizado: Presente | undefined = resposta.data?.presente;

      if (resposta.data?.desativado) {
        if (presenteAtualizado) {
          setPresentes((prev) =>
            prev.map((item) => (item.id === presenteAtualizado.id ? presenteAtualizado : item))
          );
        } else {
          await carregarDados();
        }
      } else {
        setPresentes((prev) => prev.filter((item) => item.id !== presente.id));
      }

      const mensagemResposta =
        resposta.data?.message ||
        (resposta.data?.desativado ? 'Presente desativado.' : 'Presente removido.');
      setSuccess(mensagemResposta);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Erro ao remover presente';
      setError(message);
    } finally {
      setDeletingId(null);
    }
  };

  const recarregar = async () => {
    setReloading(true);
    await carregarDados();
    setReloading(false);
  };

  const carregarCatalogo = useCallback(
    async (
      filters: ProdutoAfiliadoFilters = {},
      options: { page?: number; pageSize?: number } = {}
    ) => {
      const page = options.page ?? catalogoPage;
      const pageSize = options.pageSize ?? catalogoPageSize;

      try {
        setCatalogoLoading(true);
        setCatalogoError('');
        setCatalogoSuccess('');

        const filtrosLimpos: ProdutoAfiliadoFilters = {
          ...filters,
          page,
          pageSize,
        };

        const response = await listarCatalogoProdutos(filtrosLimpos);
        const produtos = response.produtos ?? [];
        setCatalogoProdutos(produtos);

        const total = response.total ?? produtos.length;
        const respostaPageSize = response.pageSize ?? pageSize;
        const totalPagesCalculado =
          response.totalPages ?? (total > 0 ? Math.ceil(total / respostaPageSize) : 0);
        const paginaResposta = response.page ?? page;

        setCatalogoTotal(total);
        setCatalogoTotalPages(totalPagesCalculado);
        setCatalogoPage(
          totalPagesCalculado === 0 ? 1 : Math.min(paginaResposta, totalPagesCalculado)
        );
        setCatalogoPageSize(respostaPageSize);

        const categorias = response.categoriasDisponiveis ?? [];
        setCatalogoCategorias((prev) => {
          const merged = new Set<string>([...prev, ...categorias]);
          if (catalogoFiltroCategoria) {
            merged.add(catalogoFiltroCategoria);
          }
          return Array.from(merged).sort((a, b) => a.localeCompare(b, 'pt-BR'));
        });

        if (response.faixaPreco) {
          setCatalogoFaixaPreco(response.faixaPreco);
        } else {
          setCatalogoFaixaPreco(null);
        }
      } catch (err: any) {
        const message = err?.response?.data?.error || 'Erro ao carregar catálogo de afiliados';
        setCatalogoError(message);
      } finally {
        setCatalogoLoading(false);
      }
    },
    [catalogoFiltroCategoria, catalogoPage, catalogoPageSize]
  );

  const montarFiltrosCatalogo = useCallback(
    (
      overrides: {
        search?: string;
        categoria?: string;
        faixa?: { min?: number; max?: number };
      } = {}
    ) => {
      const filtros: ProdutoAfiliadoFilters = {};
      const searchTerm = overrides.search !== undefined ? overrides.search : catalogoFiltroSearch;
      const categoriaSelecionada =
        overrides.categoria !== undefined ? overrides.categoria : catalogoFiltroCategoria;
      const faixaSelecionada =
        overrides.faixa !== undefined ? overrides.faixa : catalogoFiltroFaixa;

      if (searchTerm) filtros.search = searchTerm;
      if (categoriaSelecionada) filtros.categoria = categoriaSelecionada;
      if (typeof faixaSelecionada.min === 'number') filtros.minPreco = faixaSelecionada.min;
      if (typeof faixaSelecionada.max === 'number') filtros.maxPreco = faixaSelecionada.max;

      return filtros;
    },
    [catalogoFiltroCategoria, catalogoFiltroFaixa, catalogoFiltroSearch]
  );

  useEffect(() => {
    if (!catalogoOpen) return;
    const filtros = montarFiltrosCatalogo();
    void carregarCatalogo(filtros, { page: catalogoPage, pageSize: catalogoPageSize });
  }, [catalogoOpen, catalogoPage, catalogoPageSize, carregarCatalogo, montarFiltrosCatalogo]);

  useEffect(() => {
    if (!catalogoOpen) return;
    setCatalogoSelecionados(new Set(presentesAfiliadosIds));
  }, [catalogoOpen, presentesAfiliadosIds]);

  const abrirCatalogoAfiliado = () => {
    setCatalogoPage(1);
    setCatalogoOpen(true);
    const params = new URLSearchParams(searchParams);
    params.set('catalogo', '1');
    setSearchParams(params, { replace: true });
  };

  const fecharCatalogoAfiliado = () => {
    if (catalogoAdicionandoId) return;
    setCatalogoOpen(false);
    catalogoSearchRef.current = '';
    setCatalogoFiltroSearch('');
    setCatalogoFiltroCategoria('');
    setCatalogoFiltroFaixa({});
    setCatalogoCategorias([]);
    setCatalogoProdutos([]);
    setCatalogoSelecionados(new Set());
    setCatalogoPage(1);
    setCatalogoPageSize(12);
    setCatalogoTotal(0);
    setCatalogoTotalPages(0);
    setCatalogoFaixaPreco(null);
    setCatalogoError('');
    setCatalogoSuccess('');
    const params = new URLSearchParams(searchParams);
    if (params.has('catalogo')) {
      params.delete('catalogo');
      setSearchParams(params, { replace: true });
    }
  };

  const handleCatalogoInputChange = (valor: string) => {
    catalogoSearchRef.current = valor;
    if (debounceBuscaRef.current) {
      window.clearTimeout(debounceBuscaRef.current);
    }
    debounceBuscaRef.current = window.setTimeout(() => {
      const termo = catalogoSearchRef.current.trim();
      setCatalogoFiltroSearch(termo);
      setCatalogoPage(1);
    }, 800);
  };

  useEffect(() => {
    return () => {
      if (debounceBuscaRef.current) {
        window.clearTimeout(debounceBuscaRef.current);
      }
    };
  }, []);

  const handleCatalogoBuscar = () => {
    if (debounceBuscaRef.current) {
      window.clearTimeout(debounceBuscaRef.current);
    }
    const termo = catalogoSearchRef.current.trim();
    setCatalogoFiltroSearch(termo);
    setCatalogoPage(1);
  };

  const handleCatalogoFiltroCategoriaChange = useCallback(
    (categoria: string) => {
      const novoValor = categoria === catalogoFiltroCategoria ? '' : categoria;
      setCatalogoFiltroCategoria(novoValor);
      setCatalogoPage(1);
    },
    [catalogoFiltroCategoria]
  );

  const handleCatalogoFaixaPrecoChange = useCallback((min?: number, max?: number) => {
    setCatalogoFiltroFaixa({ min, max });
    setCatalogoPage(1);
  }, []);

  const handleCatalogoKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleCatalogoBuscar();
  };

  const adicionarProdutoAfiliado = useCallback(
    async (produto: ProdutoAfiliado) => {
      if (!listaId) return false;
      setCatalogoAdicionandoId(produto.id);
      setCatalogoError('');
      setCatalogoSuccess('');
      let sucesso = false;
      try {
        const resposta = await api.post(`/presentes/lista/${listaId}/presentes`, {
          nome: produto.nome,
          descricao: produto.descricao ?? undefined,
          valor: produto.preco,
          imagemUrl: produto.imagemUrl ?? undefined,
          categoria: produto.categoria ?? undefined,
          disponivel: true,
          produtoAfiliadoId: produto.id,
          origem: 'AFILIADO',
        });
        const presenteCriado = resposta.data?.presente;

        if (presenteCriado) {
          setPresentes((prev) => {
            const atualizado = [...prev, presenteCriado];
            return atualizado.sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
          });
        } else {
          await carregarDados();
        }

        setSuccess('Produto afiliado adicionado à sua lista!');
        setCatalogoSuccess(
          `"${produto.nome}" foi adicionado à lista. Continue escolhendo outros itens.`
        );
        sucesso = true;
      } catch (err: any) {
        const message = err?.response?.data?.error || 'Erro ao adicionar produto afiliado';
        setCatalogoError(message);
      } finally {
        setCatalogoAdicionandoId(null);
      }
      return sucesso;
    },
    [carregarDados, listaId]
  );

  const handleSelecionarProduto = useCallback(
    async (produto: ProdutoAfiliado) => {
      if (catalogoAdicionandoId && catalogoAdicionandoId !== produto.id) {
        return;
      }

      if (presentesAfiliadosIds.has(produto.id) || catalogoSelecionados.has(produto.id)) {
        return;
      }

      const sucesso = await adicionarProdutoAfiliado(produto);
      if (sucesso) {
        setCatalogoSelecionados((prev) => {
          const atualizado = new Set(prev);
          atualizado.add(produto.id);
          return atualizado;
        });
      }
    },
    [adicionarProdutoAfiliado, catalogoAdicionandoId, catalogoSelecionados, presentesAfiliadosIds]
  );

  return {
    lista,
    presentes,
    presentesFiltrados,
    loading,
    error,
    setError,
    success,
    setSuccess,
    dialogOpen,
    dialogMode,
    form,
    saving,
    deletingId,
    presentesLoadingId,
    reloading,
    catalogoOpen,
    catalogoLoading,
    catalogoProdutos,
    catalogoError,
    catalogoFiltroSearch,
    setCatalogoFiltroSearch,
    catalogoAdicionandoId,
    catalogoSuccess,
    catalogoCategorias,
    catalogoFiltroCategoria,
    catalogoSelecionados,
    catalogoPage,
    setCatalogoPage,
    catalogoTotalPages,
    catalogoFaixaPrecoLabel,
    catalogoResumoPaginacao,
    catalogoFaixaPrecoAtivaChave,
    filtrosPresentes,
    filtrosAplicadosResumo,
    categoriasDisponiveis,
    parceirosDisponiveis,
    precoChips,
    faixaPrecoAtivaChave,
    tituloLista,
    abrirDialogCriar,
    abrirDialogEditar,
    fecharDialog,
    handleFormChange,
    salvarPresente,
    removerPresente,
    alterarDisponibilidade,
    recarregar,
    abrirCatalogoAfiliado,
    fecharCatalogoAfiliado,
    handleCatalogoBuscar,
    handleCatalogoFiltroCategoriaChange,
    handleCatalogoFaixaPrecoChange,
    handleCatalogoKeyDown,
    handleCatalogoInputChange,
    handleSelecionarProduto,
    atualizarFiltroPresentes,
    handleFiltroValorChange,
    aplicarCategoriaRapida,
    aplicarParceiroRapido,
    aplicarFaixaPrecoRapida,
    limparFiltrosPresentes,
    presentesAfiliadosIds,
  };
};

export type UseAdminListaPresentesRulesReturn = ReturnType<typeof useAdminListaPresentesRules>;
